/* eslint-disable react/jsx-no-target-blank */
import React, { ReactElement, useState, useEffect } from "react";
import { CloseIcon, SquareButton } from "@themuse/design-system";
import { useAppState } from "hooks/AppContext";
import { JobApplyButton } from "./JobApplyButton";
import { JobTrackingProps, getJobTrackingProps } from "utils/tracking";

import styles from "./StickyHeader.module.scss";

interface StickyHeaderProps {
    setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    isSticky: boolean;
    isFrame?: boolean;
}

/**
 * Closes the SideBySide modal by sending a message to the top window.
 */
const closeSideBySide = () => {
    window.top.postMessage("closeSideBySide", "*");
};


/**
 * Renders the sticky header component for an individual job.
 *
 * This component displays the company name, job title, posted date, location, and company information.
 * It also tracks user interactions and applies appropriate styling based on the user's actions.
 *
 * @returns {ReactElement} The rendered header component for an individual job.
 */
export const StickyHeader = (props: StickyHeaderProps): ReactElement => {
    const { job, company } = useAppState();
    const { id, name: jobName, state, ats_info } = job;
    const { package: pkg, name: companyName } = company;
    const { directApplyService = "" } = ats_info;
    const jobTrackingProps: JobTrackingProps = getJobTrackingProps(job, company);
    const { setIsOpen, isSticky, isFrame } = props;
    const applyText = directApplyService ? "Quick Apply" : "Apply on company site";
    const buttonText = state === "live" ? applyText : "Search all jobs";
    const liveTargetSection: string = directApplyService ? "direct_apply_form" : "off_site";
    const targetSection = state === "live" ? liveTargetSection : "search";
    const trackingObj = {
        ...jobTrackingProps,
        sp_click_button_text: buttonText,
        sp_click_target_page_section: targetSection,
        sp_click_position: "sticky_nav",
        target_page_section: "direct_apply_form",
        sp_click_button_type: state === "live" ? "apply" : "job_search"
    }

    // used to test if the parent page is mobile as the Iframe has no idea.
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const handleResize = () => {
          setIsMobile(!window.parent.matchMedia("(min-width: 992px)").matches);
        };

        handleResize();

        window.addEventListener('resize', handleResize);

        return () => {
          window.removeEventListener('resize', handleResize);
        };
    }, []);

    const impressionTrackingObj = {
        ...jobTrackingProps,
        target_page_section: "direct_apply_form",
        sp_impression_component: "insert",
        sp_impression_tile_type: null,
        sp_impression_card_type: null,
        sp_impression_insert_type: "jobs_cta",
        sp_impression_modal_type: null,
        sp_impression_position: "pop_up",
        sp_impression_search_id: null,
        sp_impression_button_type: null
    };

    return (
        <div
            className={`${styles.stickyHeader} ${isSticky ? styles.open : ""} ${isFrame ? styles.framed : ""}`}
            data-tracking-event-type="impression"
            data-tracking-props={JSON.stringify(impressionTrackingObj)}
        >
            <div className={styles.content}>
                    {isFrame && isMobile ? (
                        <button  className={styles.closeIframe}>
                            <CloseIcon onClick={closeSideBySide} />
                        </button>
                    ) : null
                    }
                <div className={styles.names}>
                    <p className={styles.title}>{jobName}</p>
                    {pkg !== "Programmatic"
                    ? (
                        <a
                            href={`/profiles/${company.short_name}`}
                            className={styles.companyName}
                            target="_blank"
                        >
                            AT { companyName }
                        </a>
                    )
                        : <p className={styles.companyNameProgrammatic}>{companyName}</p>
                    }
                </div>
                <div className={styles.actions}>
                    {state === "live" ? (
                        <JobApplyButton
                            jobId={id}
                            className={styles.applyButton}
                            trackingProps={trackingObj}
                            setIsOpen={setIsOpen}
                            isDirectApply={!!directApplyService}
                        />
                    ) : null}
                    {state !== "live" ? (
                        <>
                            <p className={styles.archivedJobMessage}>This job is no longer available.</p>
                            {!isFrame ? (
                                <SquareButton
                                    className={styles.archiveButton}
                                    data-tracking-event-type="click"
                                    data-tracking-props={trackingObj}
                                    href="/search"
                                    target="_blank"
                                    color="mint"
                                >
                                    {buttonText}
                                </SquareButton>
                            ) : null}
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
StickyHeader.displayName = "StickyHeader";

export const FloatingHeader = React.memo(StickyHeader);
